<template>
  <div class="InfrastructureFicheContent">
    <!-- View mode: read-only fiche from GET /api/infrastructures/{id} -->
    <template v-if="!editMode">
      <h1 class="InfrastructureFicheContent__title">
        {{ t('infrastructure.fiche.title') }}
      </h1>
      <p v-if="idInfrastructure" class="InfrastructureFicheContent__id">
        {{ t('infrastructure.fiche.idLabel') }}:
        <strong>{{ displayId }}</strong>
      </p>
      <p v-else class="InfrastructureFicheContent__noId">
        {{ t('infrastructure.fiche.noId') }}
      </p>
      <div v-if="isLoading" class="InfrastructureFicheContent__skeleton">
        <p class="InfrastructureFicheContent__skeletonText">
          {{ t('infrastructure.fiche.loading') }}
        </p>
      </div>
      <div v-else-if="loadError" class="InfrastructureFicheContent__error">
        <p class="InfrastructureFicheContent__errorText">
          {{
            loadErrorStatus === 404
              ? t('infrastructure.fiche.notFound')
              : t('infrastructure.fiche.loadError')
          }}
        </p>
      </div>
      <div v-else-if="isLoadingAttributs" class="InfrastructureFicheContent__skeleton">
        <p class="InfrastructureFicheContent__skeletonText">
          {{ t('infrastructure.fiche.loading') }}
        </p>
      </div>
      <div v-else-if="attributEntries.length > 0" class="InfrastructureFicheContent__attributes">
        <dl class="InfrastructureFicheContent__dl">
          <template v-for="entry in attributEntries" :key="entry.atgRefCode">
            <dt class="InfrastructureFicheContent__dt">{{ entry.label }}</dt>
            <dd class="InfrastructureFicheContent__dd">{{ formatAttributValue(entry.value) }}</dd>
          </template>
        </dl>
      </div>
      <div v-else class="InfrastructureFicheContent__skeleton">
        <p class="InfrastructureFicheContent__skeletonText">
          {{ t('infrastructure.fiche.noAttributes') }}
        </p>
      </div>
      <button
        v-if="!isLoading && !loadError && infrastructure"
        type="button"
        class="InfrastructureFicheContent__editButton"
        @click="editMode = true"
      >
        {{ t('infrastructure.fiche.editButton') }}
      </button>

      <!--
        Gouvernance : analyse spatiale (§4, §3.5.3), décision du maire, interventions et rapport
        PDF (§5, §3.5). Le panneau se masque de lui-même si l'utilisateur ne gouverne pas cette
        commune — l'API répond alors 403.
      -->
      <GouvernancePanel
        v-if="!isLoading && !loadError && infrastructure"
        class="InfrastructureFicheContent__gouvernance"
        :ift-id="iftIdNumerique"
      />
    </template>

    <!-- Edit mode: form (PUT /api/infrastructures/{id}), same modal. Merge feature properties so form is pre-filled. -->
    <InfrastructureEditModal
      v-else
      :inline="true"
      :infrastructure="mergedInfrastructureForEdit"
      :id-infrastructure="idInfrastructure"
      :competence-id="competenceId"
      @close="editMode = false"
      @saved="(payload) => handleSaved(payload)"
    />
  </div>
</template>

<script setup lang="ts">
import type { Infrastructure } from '@/models/interfaces/Infrastructure'
import type { Attribut } from '@/models/interfaces/Attribut'
import { ATTRIBUT_KIND_EDITABLE } from '@/models/interfaces/Attribut'
import { InfrastructureService } from '@/services/infrastructure/InfrastructureService'
import { AttributService } from '@/services/attribut/AttributService'
import { CompetenceTypeOuvrageService } from '@/services/competenceTypeOuvrage/CompetenceTypeOuvrageService'
import InfrastructureEditModal from '@/views/infrastructure/InfrastructureEditModal.vue'
import GouvernancePanel from '@/views/infrastructure/components/GouvernancePanel.vue'
import { POSITION, useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { computed, watch, ref } from 'vue'
import { libelleDynamique } from '@/utils/libelleDynamique'

const { t } = useI18n()
const toast = useToast()

const props = defineProps<{
  idInfrastructure: string | number | null
  competenceId?: number | null
  /** Feature properties from map click (e.g. QUALITE_OUVRAGE, ETAT_FONCTIONNEL). Fills attribut values when API omits them. */
  featureProperties?: Record<string, unknown> | null
}>()

const infrastructure = ref<Infrastructure | null>(null)
const isLoading = ref(false)
const loadError = ref(false)
const loadErrorStatus = ref<number | undefined>(undefined)
const editMode = ref(false)
const availableAttributs = ref<Attribut[]>([])
const isLoadingAttributs = ref(false)

/**
 * Valeurs d'attributs enregistrées (infrastructure.inst_atg_ift), servies à plat par code :
 * { GESTIONNAIRE: 'MINEDUB', … }. GET /api/infrastructures/{id} ne les expose pas ; sans elles, la
 * fiche ouverte ailleurs que depuis un clic sur la carte (tableau de bord, lien direct) n'avait
 * aucune valeur à afficher.
 */
const valeursEnregistrees = ref<Record<string, unknown>>({})

async function chargerValeursEnregistrees(id: string | number): Promise<void> {
  try {
    const donnees = await InfrastructureService.getAttributeValues(id)
    valeursEnregistrees.value = Array.isArray(donnees) ? {} : donnees
  } catch {
    valeursEnregistrees.value = {}
  }
}

/**
 * Infrastructure merged with feature properties so view + edit show values from popup when API
 * omits them. Saved values come last: the map feature properties can lag behind the database.
 */
const mergedInfrastructureForEdit = computed(() => {
  const infra = infrastructure.value
  const feature = props.featureProperties ?? {}
  if (!infra) return null
  return {
    ...(infra as Record<string, unknown>),
    ...feature,
    ...valeursEnregistrees.value,
  } as Infrastructure
})

/** Les endpoints de gouvernance adressent l'équipement par son ift_id numérique. */
const iftIdNumerique = computed(() => {
  const brut = Number(props.idInfrastructure)
  return Number.isFinite(brut) && brut > 0 ? brut : null
})

/** Identifiant shown in fiche: ift_id from API or idInfrastructure prop. */
const displayId = computed(() => {
  const data = infrastructure.value as Record<string, unknown> | null
  if (data?.ift_id != null) return String(data.ift_id)
  if (data?.iftId != null) return String(data.iftId)
  return props.idInfrastructure != null ? String(props.idInfrastructure) : ''
})

function getTypeOuvrageCode(infra: Infrastructure | null): string | undefined {
  if (!infra || typeof infra !== 'object') return undefined
  const rec = infra as Record<string, unknown>
  const raw =
    rec.ift_type_code ??
    rec.iftTypeCode ??
    (rec.typeOuvrage && typeof rec.typeOuvrage === 'object' && rec.typeOuvrage !== null
      ? ((rec.typeOuvrage as Record<string, unknown>).code ??
        (rec.typeOuvrage as Record<string, unknown>).libelle)
      : rec.type_ouvrage)
  return raw != null ? String(raw).trim() : undefined
}

function extractAttributIds(cto: {
  attributs?: string[] | Array<{ atgId?: number; ['@id']?: string }>
}): number[] {
  const attributs = cto.attributs ?? (cto as Record<string, unknown>).attributs
  if (!Array.isArray(attributs)) return []
  return attributs
    .map((a) => {
      if (typeof a === 'string') {
        const match = a.match(/\/api\/attributs\/(\d+)/)
        return match ? parseInt(match[1], 10) : NaN
      }
      const obj = a as { atgId?: number }
      return obj?.atgId ?? (a as Record<string, unknown>).atgId
    })
    .filter((id): id is number => Number.isInteger(id))
}

function typeOuvrageMatches(
  cto: {
    typeOuvrage?: { code?: string; libelle?: string } | string
    fkCptId?: number
    fkTypeOuvrageId?: number
  },
  typeCode: string,
  competenceId: number | null,
): boolean {
  // Sans compétence connue (fiche ouverte depuis le tableau de bord), le type d'ouvrage suffit : un
  // même type relève souvent de plusieurs compétences (ECOLE en compte 7), et ses attributs sont
  // définis au niveau du type. Exiger une compétence ici écartait toutes les entrées.
  const cptId = cto.fkCptId ?? (cto as Record<string, unknown>).fk_cpt_id
  if (competenceId !== null && cptId !== competenceId) return false
  const typeOuvrage = cto.typeOuvrage ?? (cto as Record<string, unknown>).type_ouvrage
  if (!typeOuvrage) return cto.fkTypeOuvrageId != null
  const code =
    typeof typeOuvrage === 'string'
      ? typeOuvrage
      : ((typeOuvrage as { code?: string; libelle?: string }).code ??
        (typeOuvrage as { code?: string; libelle?: string }).libelle)
  return code != null && String(code).toUpperCase() === String(typeCode).toUpperCase()
}

async function loadAttributs() {
  if (!infrastructure.value) return
  isLoadingAttributs.value = true
  try {
    const typeCode = getTypeOuvrageCode(infrastructure.value)
    const competenceId = props.competenceId ?? null

    // Fast path: one call scoped to this (competence, type ouvrage) pair, instead of
    // crawling the full /api/attributs (195 rows) and /api/competence_attributs (384 rows)
    // collections page by page on every fiche display / post-save reload.
    // Le type d'ouvrage suffit : ouverte par lien direct ou depuis le tableau de bord, la fiche n'a
    // pas de compétence, et le repli ci-dessous ne peut rien trouver sans elle.
    if (typeCode) {
      availableAttributs.value =
        await CompetenceTypeOuvrageService.getAttributsForCompetenceAndType(competenceId, typeCode)
      return
    }

    // Fallback (competenceId or typeCode unavailable): full crawl.
    const [allCto, allAttributs, competenceAttributs] = await Promise.all([
      CompetenceTypeOuvrageService.getAll({ expandAttributs: true }).catch(() => []),
      AttributService.getAll(),
      AttributService.getCompetenceAttributs().catch(() => []),
    ])

    const attributIds = new Set<number>()

    if (competenceId != null) {
      competenceAttributs
        .filter(
          (ca) =>
            (ca.fkCptId ?? (ca as unknown as Record<string, unknown>).fk_cpt_id) === competenceId,
        )
        .forEach((ca) => {
          const id = ca.fkAttributId ?? (ca as unknown as Record<string, unknown>).fk_attribut_id
          if (typeof id === 'number') attributIds.add(id)
        })
    }

    if (attributIds.size === 0) {
      const matchingCto = allCto.filter((cto) =>
        typeCode ? typeOuvrageMatches(cto, typeCode, competenceId) : false,
      )
      matchingCto.forEach((cto) => extractAttributIds(cto).forEach((id) => attributIds.add(id)))
    }

    availableAttributs.value = allAttributs.filter(
      (attr) => attr.atgKind === ATTRIBUT_KIND_EDITABLE && attributIds.has(attr.atgId),
    )
  } catch (error) {
    console.error('[InfrastructureFicheContent] Error loading attributs:', error)
    availableAttributs.value = []
  } finally {
    isLoadingAttributs.value = false
  }
}

/** Keys where API may nest attribute values (e.g. GET /api/infrastructures/{id}). */
const NESTED_ATTRIBUTE_KEYS = ['attributeValues', 'instAtgIft', 'inst_atg_ift'] as const

function getValueFromFlatObject(obj: Record<string, unknown>, atgRefCode: string): unknown {
  if (obj[atgRefCode] !== undefined) return obj[atgRefCode]
  const lower = atgRefCode.toLowerCase()
  if (obj[lower] !== undefined) return obj[lower]
  const camelCase = atgRefCode
    .split('_')
    .map((part, i) =>
      i === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join('')
  if (obj[camelCase] !== undefined) return obj[camelCase]
  const key = Object.keys(obj).find(
    (k) =>
      k.toLowerCase() === lower ||
      k.replace(/_/g, '').toLowerCase() === atgRefCode.replace(/_/g, '').toLowerCase(),
  )
  return key != null ? obj[key] : undefined
}

/**
 * Resolve attribut value from infrastructure. API may return keys at top level or inside
 * nested objects (attributeValues, instAtgIft, inst_atg_ift). Tries atgRefCode, snake_case, camelCase.
 */
function getAttributValue(infra: Record<string, unknown>, atgRefCode: string): unknown {
  const topLevel = getValueFromFlatObject(infra, atgRefCode)
  if (topLevel !== undefined) return topLevel
  for (const nestedKey of NESTED_ATTRIBUTE_KEYS) {
    const nested = infra[nestedKey]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const value = getValueFromFlatObject(nested as Record<string, unknown>, atgRefCode)
      if (value !== undefined) return value
    }
  }
  return undefined
}

/** Entries to display in fiche: attributs (label + value from infrastructure, then feature properties). */
/**
 * Valeur d'un attribut de type liste, traduite.
 *
 * Ce qui est enregistré est la valeur française de l'option (agc_valeur) : la fiche affichait donc
 * « FONCTIONNEL » même en anglais. L'option est retrouvée parmi les choix de l'attribut, par sa
 * valeur ou par son code, puis rendue dans la langue courante. Une valeur qui ne correspond à
 * aucune option est laissée telle quelle : elle vient d'une saisie libre ou d'un import.
 */
function traduireValeurChoix(attr: Attribut, valeur: unknown): unknown {
  if (typeof valeur !== 'string' || !attr.choices?.length) return valeur
  const cherchee = valeur.trim().toLowerCase()
  const option = attr.choices.find(
    (c) =>
      (c.agcValeur ?? '').trim().toLowerCase() === cherchee ||
      (c.agcRefCode ?? '').trim().toLowerCase() === cherchee,
  )
  return option ? libelleDynamique(option.agcValeur, option.agcValeurEn) : valeur
}

const attributEntries = computed(() => {
  const infra = infrastructure.value as Record<string, unknown> | null
  if (!infra) return []
  const feature = props.featureProperties ?? {}
  return availableAttributs.value.map((attr) => ({
    atgRefCode: attr.atgRefCode,
    label: libelleDynamique(attr.atgTitre, attr.atgTitreEn),
    value: traduireValeurChoix(
      attr,
      getAttributValue(infra, attr.atgRefCode) ??
        getAttributValue(valeursEnregistrees.value, attr.atgRefCode) ??
        getAttributValue(feature, attr.atgRefCode),
    ),
  }))
})

function formatAttributValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (typeof value === 'object' && value !== null && '@id' in value) {
    return String((value as { '@id'?: string })['@id'] ?? '')
  }
  return JSON.stringify(value)
}

async function handleSaved(savedPayload?: Record<string, string | number | boolean>) {
  editMode.value = false
  if (props.idInfrastructure != null) {
    loadError.value = false
    loadErrorStatus.value = undefined
    isLoading.value = true
    try {
      const [refetched] = await Promise.all([
        InfrastructureService.getById(
          props.idInfrastructure,
          true, // bust cache so fiche shows fresh data after save
        ),
        chargerValeursEnregistrees(props.idInfrastructure),
      ])
      const infraRecord = refetched as Record<string, unknown>
      if (savedPayload && Object.keys(savedPayload).length > 0) {
        infrastructure.value = { ...infraRecord, ...savedPayload } as Infrastructure
      } else {
        infrastructure.value = refetched
      }
      await loadAttributs()
    } catch (err: unknown) {
      loadError.value = true
      const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })
        ?.response
      loadErrorStatus.value = res?.status
      const loadMsg =
        res?.status === 404
          ? t('infrastructure.fiche.notFound')
          : t('infrastructure.fiche.loadError')
      toast.error(loadMsg, { position: POSITION.TOP_RIGHT })
      if (savedPayload && Object.keys(savedPayload).length > 0) {
        infrastructure.value = {
          ...(infrastructure.value as Record<string, unknown>),
          ...savedPayload,
        } as Infrastructure
      }
    } finally {
      isLoading.value = false
    }
  }
}

watch(
  () => props.idInfrastructure,
  async (id) => {
    infrastructure.value = null
    loadError.value = false
    loadErrorStatus.value = undefined
    editMode.value = false
    availableAttributs.value = []
    valeursEnregistrees.value = {}
    if (id == null) return
    isLoading.value = true
    try {
      const [infra] = await Promise.all([
        InfrastructureService.getById(id),
        chargerValeursEnregistrees(id),
      ])
      infrastructure.value = infra
    } catch (err: unknown) {
      loadError.value = true
      const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })
        ?.response
      loadErrorStatus.value = res?.status
      const loadMsg =
        res?.status === 404
          ? t('infrastructure.fiche.notFound')
          : t('infrastructure.fiche.loadError')
      toast.error(loadMsg, { position: POSITION.TOP_RIGHT })
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true },
)

watch(
  () => [infrastructure.value, props.competenceId],
  () => {
    if (infrastructure.value) loadAttributs()
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
.InfrastructureFicheContent {
  display: flex;
  flex-flow: column nowrap;
  gap: 1.25rem;
  font-family: inherit;

  &__title {
    font-size: 1.375rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: rgb(var(--v-theme-main-blue));
    margin: 0;
    line-height: 1.3;
  }

  &__id,
  &__noId {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0;
    font-weight: 500;

    strong {
      font-weight: 600;
      color: #374151;
    }
  }

  &__skeleton {
    padding: 1.5rem 1.25rem;
    background: #f8fafc;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
  }

  &__skeletonText {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0;
  }

  &__error {
    padding: 1.25rem 1.25rem;
    background: #fef2f2;
    border-radius: 0.75rem;
    border: 1px solid #fecaca;
  }

  &__errorText {
    font-size: 0.875rem;
    color: #b91c1c;
    margin: 0;
  }

  &__attributes {
    margin-top: 0.25rem;
    background: #ffffff;
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    padding: 0 1rem;
  }

  &__dl {
    display: grid;
    grid-template-columns: minmax(10rem, auto) minmax(8rem, 1fr);
    margin: 0;
    padding: 0.25rem 0 0 0;
    gap: 0 1.5rem;
  }

  &__dt {
    margin: 0;
    padding: 0.625rem 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6b7280;
    letter-spacing: 0.02em;
    border-bottom: 1px solid #f3f4f6;
    line-height: 1.45;
  }

  &__dd {
    margin: 0;
    padding: 0.625rem 0;
    font-size: 0.875rem;
    color: #111827;
    word-break: break-word;
    border-bottom: 1px solid #f3f4f6;
    line-height: 1.45;
    font-weight: 500;
  }

  &__dl > *:nth-last-child(-n + 2) {
    border-bottom: none;
  }

  &__editButton {
    margin-top: 0.5rem;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: var(--fiche-button-bg);
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition:
      box-shadow 0.2s ease,
      transform 0.1s ease,
      background-color 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    &:hover {
      background: var(--fiche-button-hover);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }
}
</style>
